import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControlOptions,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { IFormField, IFormFieldChoice, FormFieldType } from '@app/run-planning/model/form-field';
import { getDefaultValue } from '@app/run-planning/services/helper.service';
import { VALIDATION_RULE } from '@app/cloud-run-prep/constants';
import { FormValueObject } from '@app/run-planning/interface';
import { get, isInteger } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-fieldgroup-table',
  templateUrl: './fieldgroup-table.component.html',
  styleUrls: ['./fieldgroup-table.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FieldgroupTableComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FieldgroupTableComponent), multi: true }
  ]
})
export class FieldgroupTableComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() columns: IFormField[] = [];
  @Input() fieldGroupId: string;
  @Input() minRows: number;
  @Input() maxRows: number;
  @Input() allSettingsFormValues: FormValueObject;
  @Input() analysisVersionDefinitionId: string = '';
  @Input() analysisLocation: string;
  @Input() showHyperlinkInDescription: boolean = true;
  @Input() disabled: boolean = false;

  rowsFormArray: FormArray;

  get rowFormGroups(): FormGroup[] {
    return this.rowsFormArray.controls as FormGroup[];
  }

  /**
   * Seed observable required by ReferenceFileDropdownComponent.
   * The dropdown uses combineLatest(genomeId$, this input) internally,
   * so it needs at least one emission to begin loading options.
   * Also serves as a refresh trigger if a new reference file is uploaded.
   */
  private referenceFileRefreshSubject = new BehaviorSubject<string>(null);
  referenceFileRefresh$ = this.referenceFileRefreshSubject.asObservable();

  private subs = new SubSink();
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private suppressEmit = false;

  constructor(private formBuilder: FormBuilder) {
    this.rowsFormArray = new FormArray([]);
  }

  ngOnInit() {
    this.subs.sink = this.rowsFormArray.valueChanges.subscribe(() => {
      this.emitValue();
    });

    if (this.rowsFormArray.length === 0 && this.minRows != null) {
      for (let i = 0; i < this.minRows; i++) {
        const rowGroup = this.buildRowFormGroup();
        this.rowsFormArray.push(rowGroup);
      }
      this.emitValue();
    }
    this.updateReferenceFileGenomeDependencies();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.allSettingsFormValues) {
      this.updateReferenceFileGenomeDependencies();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  writeValue(value: any): void {
    this.suppressEmit = true;
    while (this.rowsFormArray.length > 0) {
      this.rowsFormArray.removeAt(0);
    }

    if (value && Array.isArray(value) && value.length > 0) {
      value.forEach((rowValues: any) => {
        const rowGroup = this.buildRowFormGroup(rowValues);
        this.rowsFormArray.push(rowGroup);
      });
    } else if (this.minRows != null) {
      for (let i = 0; i < this.minRows; i++) {
        const rowGroup = this.buildRowFormGroup();
        this.rowsFormArray.push(rowGroup);
      }
    }
    this.suppressEmit = false;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.rowsFormArray.disable({ emitEvent: false });
    } else {
      this.rowsFormArray.enable({ emitEvent: false });
    }
  }

  validate(): ValidationErrors | null {
    if (this.rowsFormArray.invalid) {
      return { invalid: true };
    }
    if (this.minRows != null && this.rowsFormArray.length < this.minRows) {
      return { minRows: true };
    }
    return null;
  }

  addRow(): void {
    if (this.disabled || this.isMaxRowsReached()) {
      return;
    }
    const rowGroup = this.buildRowFormGroup();
    this.rowsFormArray.push(rowGroup);
    this.emitValue();
  }

  removeRow(index: number): void {
    if (this.disabled || this.isMinRowsReached()) {
      return;
    }
    this.rowsFormArray.removeAt(index);
    this.emitValue();
  }

  isMinRowsReached(): boolean {
    return this.minRows != null && this.rowsFormArray.length <= this.minRows;
  }

  isMaxRowsReached(): boolean {
    return this.maxRows != null && this.rowsFormArray.length >= this.maxRows;
  }

  getErrorMessage(row: FormGroup, field: IFormField): string {
    const control = row.get(field.id);
    if (!control || !control.touched || !control.errors) {
      return null;
    }
    if (control.errors.required) {
      return field.label + ' is required';
    }
    if (control.errors.pattern && field.regex && field.type !== 'integer') {
      return field.regexErrorMessage || ('Value must match this pattern: ' + field.regex);
    }
    if (field.type === 'integer') {
      let msg = 'Value must be an integer ';
      if (isInteger(field.max)) {
        msg += 'between ' + (field.min || 0) + ' and ' + field.max;
      } else {
        msg += 'greater than or equal to ' + (field.min || 0);
      }
      return msg;
    }
    if (field.type === 'number') {
      let msg = 'Value must be a number ';
      if (control.errors.min || control.errors.max) {
        if (isInteger(field.min) && isInteger(field.max)) {
          msg += 'between ' + field.min + ' and ' + field.max;
        } else if (control.errors.min) {
          msg += 'greater than or equal to ' + field.min;
        } else if (control.errors.max) {
          msg += 'lesser than or equal to ' + field.max;
        }
      }
      return msg;
    }
    return null;
  }

  filterChoices(choices: IFormFieldChoice[]): IFormFieldChoice[] {
    return choices ? choices.filter(choice => get(choice, 'valid', true)) : [];
  }

  onCheckBoxChange(event: any, row: FormGroup, fieldId: string): void {
    const control = row.get(fieldId);
    control.markAsTouched();
    control.patchValue(!!event.target.checked);
  }

  private updateReferenceFileGenomeDependencies(): void {
    if (!this.columns) {
      return;
    }
    for (const col of this.columns) {
      if (col.type === FormFieldType.REFERENCE_FILE && col.settings && col.settings.referenceFileGenomeFilterFieldId) {
        col.genomeFilterGenomeId = get(this.allSettingsFormValues, col.settings.referenceFileGenomeFilterFieldId);
      }
    }
  }

  private buildRowFormGroup(existingValues?: any): FormGroup {
    const rowGroup = this.formBuilder.group({});

    for (const field of this.columns) {
      const validationRules = field.required ? [Validators.required] : [];

      if (isInteger(field.max)) {
        validationRules.push(Validators.max(field.max));
      }
      if (isInteger(field.min)) {
        validationRules.push(Validators.min(field.min));
      }
      if (field.regex) {
        validationRules.push(Validators.pattern(field.regex));
      }
      if (field.type === 'number') {
        validationRules.push(Validators.pattern(VALIDATION_RULE.number.pattern));
      }
      if (field.type === 'integer') {
        validationRules.push(Validators.pattern(VALIDATION_RULE.integer.pattern));
      }

      let value = existingValues ? existingValues[field.id] : getDefaultValue(field);

      if ((field.type === 'checkbox') && field.required && !value) {
        value = false;
      }

      const options: AbstractControlOptions = {
        validators: validationRules
      };

      if (field.type === 'text' || field.type === 'textbox'
        || field.type === 'number' || field.type === 'integer') {
        options.updateOn = 'blur';
      }

      rowGroup.addControl(field.id, this.formBuilder.control(
        { value, disabled: field.disabled }, options));

      if (field.type === 'select') {
        rowGroup.get(field.id).setValue(value, { emitEvent: false, onlySelf: true });
      }
    }

    return rowGroup;
  }

  private emitValue(): void {
    if (this.suppressEmit) {
      return;
    }
    const value = this.rowsFormArray.getRawValue();
    this.onChange(value);
    this.onTouched();
  }
}
